/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import ListPlaceholder from 'src/components/Dashboard/ListPlaceholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { PropertyList } from 'src/components/Property';

import getPropertyListRetryVariables from './helpers/getPropertyListRetryVariables';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'property.property_list.initial_load_error.message',
    defaultMessage: 'An error occurred while loading properties. Please try again.',
  },
});

export const propertyListRouteQuery = graphql`
  query PropertyListRoute_Query($count: Int, $cursor: String) {
    viewer {
      ...PropertyList_viewer @arguments(count: $count, cursor: $cursor)
    }
  }
`;

/**
 * Renders `PropertyList` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} property list route content.
 */
function PropertyListPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(propertyListRouteQuery, queryRef);

  return (
    <PropertyList
      {...routeProps}
      {...queryData}
    />
  );
}

PropertyListPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `PropertyList` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @returns {React.ReactNode} property list route content.
 */
function PropertyListRouteRenderer({ routeProps }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    propertyListRouteQuery,
    routeProps,
    { logLabel: 'PropertyListRoute', getRetryVariablesFunc: getPropertyListRetryVariables },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      placeholderComponent={ListPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {queryRef ? (
          <PropertyListPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <PropertyList {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PropertyListRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default PropertyListRouteRenderer;
